import './instrument';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Analytics } from '@vercel/analytics/react';
import App from './App';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import './index.css';

import { showErrorToast, showUpdateBanner } from '@/lib/toast';
import { hasUnsavedWork } from '@/lib/unsavedWork';

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if the mutation already has its own onError handler
      if (mutation.options.onError) return;
      const msg =
        (error as any)?.response?.data?.error?.message ||
        (error as Error)?.message ||
        'Operation failed. Please try again.';
      showErrorToast(msg);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

// Inject mutation error toast container into DOM
const toastEl = document.createElement('div');
toastEl.id = 'mutation-error-toast';
toastEl.style.cssText =
  'display:none;position:fixed;top:1rem;right:1rem;z-index:9999;max-width:24rem;padding:0.75rem 1rem;border-radius:0.5rem;background:#dc2626;color:#fff;font-size:0.875rem;box-shadow:0 4px 12px rgba(0,0,0,0.15);transition:opacity 0.3s;opacity:0;';
const successToastEl = document.createElement('div');
successToastEl.id = 'success-toast';
successToastEl.style.cssText =
  'display:none;position:fixed;top:1rem;right:1rem;z-index:9999;max-width:24rem;padding:0.75rem 1rem;border-radius:0.5rem;background:#16a34a;color:#fff;font-size:0.875rem;box-shadow:0 4px 12px rgba(0,0,0,0.15);transition:opacity 0.3s;opacity:0;';
const style = document.createElement('style');
style.textContent = '#mutation-error-toast.show{opacity:1}#success-toast.show{opacity:1}';
document.head.appendChild(style);
document.body.appendChild(toastEl);
document.body.appendChild(successToastEl);

// Stale-chunk-after-deploy: when a lazy import 404s because a new build
// replaced its hashed chunk, Vite emits `vite:preloadError`. Per Vite, calling
// `event.preventDefault()` SUPPRESSES the rethrow — so the failed import never
// reaches React/Suspense/PageErrorBoundary. We therefore branch on unsaved work:
//   - clean surface  → let the error recover automatically by reloading now
//                      (matches the boundaries' prior behaviour; no data to lose).
//   - dirty surface  → preventDefault() to keep the page alive, and show a
//                      passive banner so the user can save first, then refresh.
// We never blind-reload a dirty surface.
window.addEventListener('vite:preloadError', (event) => {
  if (hasUnsavedWork()) {
    event.preventDefault();
    showUpdateBanner('Versi baharu tersedia / New version available', 'Refresh', () =>
      window.location.reload(),
    );
  } else {
    window.location.reload();
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
          <Analytics />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
